// The room's language: read once per page load from the address, the saved
// choice and the browser (src/core/i18n.ts says in which order), and the
// switch that saves another choice. `t` is the room's way to a sentence.

import {
  cookieValue, DEFAULT_LANG, LANG_COOKIE, LANG_COOKIE_SECONDS, LANG_PARAM, localePath, parseLang, resolveLang, translate,
  type Lang, type MessageKey, type Params,
} from "../core/i18n.ts";

let current: Lang | null = null;

/**
 * The saved choice's cookie name. Over HTTPS every cookie the Studio's host
 * reads carries the `__Host-` prefix (server/cookies.ts), so a sibling game
 * host cannot set it; over plain http (the dev server) the name is bare.
 */
function cookieName(): string {
  return location.protocol === "https:" ? `__Host-${LANG_COOKIE}` : LANG_COOKIE;
}

/**
 * The language saved on this host, or null. The plain name is read too: the
 * PocketJS player saves its choice under it on the Studio's host (`/play/`).
 */
export function savedLang(): Lang | null {
  if (typeof document === "undefined") return null;
  return parseLang(cookieValue(document.cookie, cookieName())) ?? parseLang(cookieValue(document.cookie, LANG_COOKIE));
}

/** Save `lang` as this visitor's choice on this host, for a year. */
export function saveLang(lang: Lang): void {
  if (typeof document === "undefined") return;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${cookieName()}=${lang}; Path=/; Max-Age=${LANG_COOKIE_SECONDS}; SameSite=Lax${secure}`;
}

/**
 * The page's language. The first call reads it: a `?lang=` in the address is
 * saved as the choice and taken out of the address, so a shared link does
 * not carry it on.
 */
export function lang(): Lang {
  if (current) return current;
  if (typeof location === "undefined") return (current = DEFAULT_LANG);
  const url = new URL(location.href);
  const asked = parseLang(url.searchParams.get(LANG_PARAM));
  if (url.searchParams.has(LANG_PARAM)) {
    if (asked) saveLang(asked);
    url.searchParams.delete(LANG_PARAM);
    history.replaceState(history.state, "", url);
  }
  current = resolveLang({ query: asked, path: location.pathname, saved: savedLang(), languages: navigator.languages ?? [navigator.language] });
  document.documentElement.lang = current;
  return current;
}

/** The sentence `key` in the page's language. */
export function t(key: MessageKey, params?: Params): string {
  return translate(lang(), key, params);
}

/**
 * Change the language: save it and show this page again in it. A public page
 * with a language prefix moves to its counterpart; the room, which keeps one
 * address, loads again.
 */
export function chooseLang(next: Lang): void {
  saveLang(next);
  const path = location.pathname;
  const target = path.startsWith("/studio") ? path : localePath(next, path);
  if (target === path) location.reload();
  else location.assign(target + location.search + location.hash);
}

/** `fetch` that names the page's language to the Studio (`x-pocket-lang`), for answers written in it. */
export const langFetch: typeof globalThis.fetch = Object.assign(
  (input: RequestInfo | URL, init?: RequestInit) => {
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set("x-pocket-lang", lang());
    return globalThis.fetch(input, { ...init, headers });
  },
  { preconnect: () => undefined },
) as typeof globalThis.fetch;

/** `href` on the Studio's host, in the page's language: a public page's prefix, or `?lang=` for the room and a game host. */
export function localized(href: string): string {
  const now = lang();
  const url = new URL(href, location.href);
  const own = url.host === location.host;
  if (own && url.pathname.startsWith("/api/")) return href;
  if (own && !url.pathname.startsWith("/studio")) url.pathname = localePath(now, url.pathname);
  else if (now !== DEFAULT_LANG) url.searchParams.set(LANG_PARAM, now);
  return own && href.startsWith("/") ? url.pathname + url.search + url.hash : url.toString();
}
